import React, { useState, useEffect, useCallback, useMemo } from "react";
import { adminApi } from "../api";
import AdminSelect from "./AdminSelect";
import PasswordInput from "./PasswordInput";
import EnvironmentSubTabs from "./EnvironmentSubTabs";
import useUrlState from "../hooks/useUrlState";
import { QRCodeSVG } from "qrcode.react";
import { formatDateAU } from "../utils/formatDate";
// Own our styles so the component renders correctly regardless of which route
// mounts it (previously it relied on the admin venue page importing this CSS).
import "./DigitalCardTab.css";

/* ── SVG icon helpers ────────────────────────── */
const Ico = {
  apple: <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.64-2.2.46-3.06-.4C3.79 16.17 4.36 9.53 8.71 9.28c1.24.06 2.11.72 2.84.76.99-.2 1.94-.78 3-.66 1.28.15 2.24.73 2.87 1.78-2.49 1.53-1.9 4.89.38 5.83-.46 1.21-1.06 2.41-1.75 3.29zM12.03 9.2C11.88 7.06 13.58 5.3 15.6 5.14c.28 2.42-2.17 4.22-3.57 4.06z"/></svg>,
  google: <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 11v2.4h3.97c-.16 1.03-1.2 3.02-3.97 3.02-2.39 0-4.34-1.98-4.34-4.42S9.61 7.58 12 7.58c1.36 0 2.27.58 2.79 1.08l1.9-1.83C15.47 5.69 13.89 5 12 5 8.13 5 5 8.13 5 12s3.13 7 7 7c4.04 0 6.72-2.84 6.72-6.84 0-.46-.05-.81-.11-1.16H12z"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  ext: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
};

/* Source fields available for mapping → Litecard dataFields. The
   `value` is the key on the member-data object passed to
   buildCardPayload. Split into shared / provider-specific so the
   mapping UI only offers fields whose values actually flow through
   the pipeline for this venue's provider.
   (buildMemberDataForCard in services/litecard.js produces the shared
   keys for both providers; IGT identification fields come from the
   IGT-only `members.identification_*` columns populated at signup.) */
const COMMON_FIELDS = [
  { value: "",                     label: "— Not mapped —" },
  // Identity
  { value: "membershipId",         label: "Membership Number" },
  { value: "firstName",            label: "First Name" },
  { value: "lastName",             label: "Last Name" },
  { value: "title",                label: "Title" },
  { value: "gender",               label: "Gender" },
  { value: "dateOfBirth",          label: "Date of Birth" },
  // Contact
  { value: "email",                label: "Email" },
  { value: "mobile",               label: "Mobile Phone" },
  { value: "telephone",            label: "Landline" },
  // Membership
  { value: "dateExpiry",           label: "Expiry Date" },
  { value: "dateOfJoining",        label: "Join Date" },
  { value: "memberStatus",         label: "Member Status" },
  { value: "productName",          label: "Membership Type / Product" },
  // Address
  { value: "streetAddress",        label: "Street Address" },
  { value: "suburb",               label: "Suburb" },
  { value: "state",                label: "State" },
  { value: "postCode",             label: "Postcode" },
  { value: "occupation",           label: "Occupation" },
  // Other
  { value: "barCode",              label: "Bar Code" },
];

/* IGT-only — identification documents collected at signup and
   persisted on `members`. Not on S7000 venues (our S7000 flow doesn't
   capture these separately, so they'd always render blank). */
const IGT_FIELDS = [
  { value: "identificationType",   label: "ID Type" },
  { value: "identificationNumber", label: "ID Number" },
  { value: "identificationExpiry", label: "ID Expiry" },
];

/* S7000 currently has no extras beyond the common set. Add fields
   here if we later want to expose card_number (physical card # from
   V_PUB_CARDS) or a specific analysis-code as a mappable source. */
const S7000_FIELDS = [];

/* CircleScan addresses are flat (Address + Suburb + PostCode) with no State
   component, so don't offer a mapping that would always render blank. */
const CIRCLESCAN_EXCLUDE = new Set(["state"]);

function sourceFieldsFor(providerType) {
  if (providerType === "igt")   return [...COMMON_FIELDS, ...IGT_FIELDS];
  if (providerType === "s7000") return [...COMMON_FIELDS, ...S7000_FIELDS];
  if (providerType === "circlescan") return COMMON_FIELDS.filter((f) => !CIRCLESCAN_EXCLUDE.has(f.value));
  return COMMON_FIELDS;
}

/* Human-label the raw Litecard dataField location codes */
function locationLabel(loc) {
  if (!loc) return "—";
  return loc
    .replace(/_FIELD$/, "")
    .replace(/_MODULE_DATA$/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

/* Shallow equal for flat { [k]: string } objects */
function shallowEq(a, b) {
  if (a === b) return true;
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  return ak.every(k => a[k] === b[k]);
}

export default function DigitalCardTab({ venueId, venueName, providerType = "s7000", litecardConfig, api = adminApi }) {
  // Provider-scoped source fields — IGT venues see ID Type/Number/Expiry,
  // S7000 venues don't (those fields never flow through their pipeline).
  const sourceFields  = useMemo(() => sourceFieldsFor(providerType), [providerType]);
  const sourceByValue = useMemo(() => sourceFields.reduce((m, s) => ((m[s.value] = s), m), {}), [sourceFields]);
  // URL-persisted sub-section so refresh / shared links stick.
  const [section, setSection] = useUrlState("dc", "credentials");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", msg: "" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Credential form — mode-aware. The same flat state holds both live
  // and sandbox keys; lcKey() picks the right one for the active sub-tab.
  const [credsEnv, setCredsEnv] = useState("live");
  const lcKey = (base) => credsEnv === "sandbox" ? `sandbox_${base}` : base;

  const [creds, setCreds] = useState({
    lc_auth_mode: litecardConfig?.lc_auth_mode || "client_credentials",
    lc_base_url:  litecardConfig?.lc_base_url  || "https://bff-api.demo.litecard.io",
    lc_username:  litecardConfig?.lc_username  || "",
    lc_password:  litecardConfig?.lc_password  || "",
    lc_client_id: litecardConfig?.lc_client_id || "",
    lc_client_secret: litecardConfig?.lc_client_secret || "",
    lc_business_id:   litecardConfig?.lc_business_id   || "",
    enabled: litecardConfig?.enabled ?? false,

    sandbox_lc_auth_mode: litecardConfig?.sandbox_lc_auth_mode || "client_credentials",
    sandbox_lc_base_url:  litecardConfig?.sandbox_lc_base_url  || "https://bff-api.demo.litecard.io",
    sandbox_lc_username:  litecardConfig?.sandbox_lc_username  || "",
    sandbox_lc_password:  litecardConfig?.sandbox_lc_password  || "",
    sandbox_lc_client_id: litecardConfig?.sandbox_lc_client_id || "",
    sandbox_lc_client_secret: litecardConfig?.sandbox_lc_client_secret || "",
    sandbox_lc_business_id:   litecardConfig?.sandbox_lc_business_id   || "",
  });

  // Live template + stored mapping
  const [linkedTemplateId, setLinkedTemplateId] = useState(litecardConfig?.lc_template_id || null);
  const [liveTemplate, setLiveTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [savedMapping, setSavedMapping] = useState({});
  const [mapping, setMapping] = useState({});

  // Template browser modal
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [selectingId, setSelectingId] = useState(null);

  // Preview
  const [previewing, setPreviewing] = useState(false);
  const [previewLinks, setPreviewLinks] = useState(null);

  // Passes list
  const [passes, setPasses] = useState([]);
  const [passTotal, setPassTotal] = useState(0);
  const [passPage, setPassPage] = useState(0);

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback({ type: "", msg: "" }), type === "error" ? 10000 : 4000);
  };

  useEffect(() => {
    setLinkedTemplateId(litecardConfig?.lc_template_id || null);
    if (litecardConfig) {
      setCreds({
        lc_auth_mode:     litecardConfig.lc_auth_mode     || "client_credentials",
        lc_base_url:      litecardConfig.lc_base_url      || "https://bff-api.demo.litecard.io",
        lc_username:      litecardConfig.lc_username      || "",
        lc_password:      litecardConfig.lc_password      || "",
        lc_client_id:     litecardConfig.lc_client_id     || "",
        lc_client_secret: litecardConfig.lc_client_secret || "",
        lc_business_id:   litecardConfig.lc_business_id   || "",
        enabled:          litecardConfig.enabled ?? false,
        sandbox_lc_auth_mode:     litecardConfig.sandbox_lc_auth_mode     || "client_credentials",
        sandbox_lc_base_url:      litecardConfig.sandbox_lc_base_url      || "https://bff-api.demo.litecard.io",
        sandbox_lc_username:      litecardConfig.sandbox_lc_username      || "",
        sandbox_lc_password:      litecardConfig.sandbox_lc_password      || "",
        sandbox_lc_client_id:     litecardConfig.sandbox_lc_client_id     || "",
        sandbox_lc_client_secret: litecardConfig.sandbox_lc_client_secret || "",
        sandbox_lc_business_id:   litecardConfig.sandbox_lc_business_id   || "",
      });
    }
  }, [litecardConfig]);

  /* ── Live template + mapping load ────────── */
  const loadLiveTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const r = await api.getLitecardTemplate(venueId);
      setLinkedTemplateId(r.template_id || null);
      setLiveTemplate(r.template || null);
      setTemplateError(r.template_error || null);
      setSavedMapping(r.field_mapping || {});
      setMapping(r.field_mapping || {});
    } catch (err) {
      setLiveTemplate(null);
      setSavedMapping({});
      setMapping({});
      setTemplateError(err.message);
      flash("error", "Failed to load template from Litecard: " + err.message);
    }
    setTemplateLoading(false);
  }, [venueId]);

  useEffect(() => { if (section === "template") loadLiveTemplate(); }, [section, loadLiveTemplate]);

  /* ── Credentials ─────────────────────────── */
  async function saveCreds(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateLitecard(venueId, creds);
      flash("success", "Litecard credentials saved");
    } catch (err) { flash("error", err.message); }
    setSaving(false);
  }

  async function testConnection() {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.testLitecard(venueId);
      setTestResult({ ok: true, msg: r.message || "Success" });
    } catch (err) { setTestResult({ ok: false, msg: err.message }); }
    setTesting(false);
  }

  /* ── Browse + select template ────────────── */
  async function openBrowse() {
    setBrowseOpen(true);
    setBrowseLoading(true);
    setRemoteTemplates([]);
    try {
      const r = await api.listLitecardTemplates(venueId);
      setRemoteTemplates(r.templates || []);
    } catch (err) {
      flash("error", err.message);
      setBrowseOpen(false);
    }
    setBrowseLoading(false);
  }

  async function selectTemplate(templateId) {
    setSelectingId(templateId);
    try {
      await api.selectLitecardTemplate(venueId, templateId);
      setLinkedTemplateId(templateId);
      setBrowseOpen(false);
      flash("success", "Template linked — edit the design in the Litecard portal");
      // When switching templates, stored mapping is likely stale. We reload
      // the saved mapping (probably empty) so the UI shows a clean slate.
      await loadLiveTemplate();
    } catch (err) {
      flash("error", err.message);
    }
    setSelectingId(null);
  }

  /* ── Field mapping ───────────────────────── */
  const dirty = useMemo(() => !shallowEq(mapping, savedMapping), [mapping, savedMapping]);

  function setMap(templateKey, sourceKey) {
    setMapping(m => {
      const next = { ...m };
      if (!sourceKey) delete next[templateKey];
      else next[templateKey] = sourceKey;
      return next;
    });
  }

  function revertMapping() {
    setMapping(savedMapping);
  }

  async function saveMapping() {
    setSaving(true);
    try {
      const r = await api.saveFieldMapping(venueId, mapping);
      setSavedMapping(r.field_mapping || mapping);
      flash("success", "Field mapping saved");
    } catch (err) { flash("error", err.message); }
    setSaving(false);
  }

  /* ── Preview ─────────────────────────────── */
  async function generatePreview() {
    setPreviewing(true); setPreviewLinks(null);
    try {
      const r = await api.previewLitecardCard(venueId);
      setPreviewLinks(r);
      flash("success", "Preview card created — scan the QR or open a wallet link below");
    } catch (err) { flash("error", err.message); }
    setPreviewing(false);
  }

  /* ── Passes ──────────────────────────────── */
  const [passesLoading, setPassesLoading] = useState(false);
  const [passesError, setPassesError] = useState("");
  const loadPasses = useCallback(async (page = 0) => {
    setPassesLoading(true); setPassesError("");
    try {
      const r = await api.getVenuePasses(venueId, { limit: 25, offset: page * 25 });
      setPasses(r.passes || []);
      setPassTotal(r.total || 0);
      setPassPage(page);
    } catch (err) { setPassesError(err.message || "Failed to load passes."); }
    finally { setPassesLoading(false); }
  }, [venueId]);

  useEffect(() => { if (section === "passes") loadPasses(0); }, [section, loadPasses]);

  async function togglePassStatus(pass) {
    const next = pass.status === "ACTIVE" ? "DEACTIVATED" : "ACTIVE";
    try {
      await api.updatePassStatus(venueId, pass.id, next);
      loadPasses(passPage);
    } catch (err) { flash("error", err.message); }
  }

  const [syncingPasses, setSyncingPasses] = useState(false);
  async function syncPassesFromLitecard() {
    setSyncingPasses(true);
    try {
      const r = await api.syncVenuePasses(venueId);
      flash("success", `Synced from Litecard — ${r.inserted} new, ${r.updated} updated (${r.total} total).`);
      loadPasses(0);
    } catch (err) {
      flash("error", err.message || "Sync failed");
    } finally {
      setSyncingPasses(false);
    }
  }

  const isLinked = !!linkedTemplateId;

  const sections = [
    { id: "credentials", label: "Credentials" },
    { id: "template", label: "Template & Mapping" },
    { id: "passes", label: "Issued Passes" },
  ];

  // Required template fields with no mapping — used for the "incomplete" warning.
  const missingRequired = useMemo(() => {
    if (!liveTemplate) return [];
    return (liveTemplate.dataFields || [])
      .filter(f => f?.formFieldSettings?.rules?.includes("required"))
      .filter(f => !mapping[f.key])
      .map(f => f.label || f.key);
  }, [liveTemplate, mapping]);

  return (
    <div className="digital-card-tab">
      {feedback.msg && <div className={`toast toast-${feedback.type}`} style={{ marginBottom: "1rem" }}>{feedback.msg}</div>}

      <div className="config-layout">
        <div className="config-nav">
          {sections.map(s => (
            <button key={s.id} className={`config-nav-item ${section === s.id ? "active" : ""}`} onClick={() => setSection(s.id)}>
              <span>{s.label}</span>
              {s.id === "credentials" && (testResult?.ok || litecardConfig?.lc_verified_at) && <span className="config-nav-dot done" />}
              {s.id === "template" && isLinked && missingRequired.length === 0 && <span className="config-nav-dot done" />}
            </button>
          ))}
        </div>

        <div className="config-panel">

          {/* ── Credentials ───────────────────── */}
          {section === "credentials" && (
            <form className="form-section" onSubmit={saveCreds}>
              <div className="section-header-row">
                <div>
                  <h2>Litecard Credentials</h2>
                  <p className="section-desc">Connect to your Litecard account for digital wallet passes</p>
                </div>
              </div>

              <EnvironmentSubTabs value={credsEnv} onChange={setCredsEnv} />

              <div className="form-field">
                <label>Authentication Mode</label>
                <AdminSelect
                  value={creds[lcKey("lc_auth_mode")]}
                  onChange={v => setCreds({ ...creds, [lcKey("lc_auth_mode")]: v })}
                  options={[
                    { value: "client_credentials", label: "Client Credentials (OAuth2)" },
                    { value: "user_pass", label: "Username & Password" },
                  ]}
                />
              </div>

              <div className="form-field">
                <label>Environment</label>
                <AdminSelect
                  value={creds[lcKey("lc_base_url")]}
                  onChange={v => setCreds({ ...creds, [lcKey("lc_base_url")]: v })}
                  options={[
                    { value: "https://bff-api.demo.litecard.io",       label: "Demo (bff-api.demo.litecard.io)" },
                    { value: "https://bff-api.enterprise.litecard.io", label: "Production (bff-api.enterprise.litecard.io)" },
                  ]}
                />
              </div>

              {creds[lcKey("lc_auth_mode")] === "user_pass" ? (
                <div className="form-grid-2">
                  <div className="form-field"><label>Username</label><input value={creds[lcKey("lc_username")] || ""} onChange={e => setCreds({ ...creds, [lcKey("lc_username")]: e.target.value })} /></div>
                  <div className="form-field"><label>Password</label><PasswordInput value={creds[lcKey("lc_password")] || ""} onChange={e => setCreds({ ...creds, [lcKey("lc_password")]: e.target.value })} /></div>
                </div>
              ) : (
                <div className="form-grid-2">
                  <div className="form-field"><label>Client ID</label><input value={creds[lcKey("lc_client_id")] || ""} onChange={e => setCreds({ ...creds, [lcKey("lc_client_id")]: e.target.value })} /></div>
                  <div className="form-field"><label>Client Secret</label><PasswordInput value={creds[lcKey("lc_client_secret")] || ""} onChange={e => setCreds({ ...creds, [lcKey("lc_client_secret")]: e.target.value })} /></div>
                </div>
              )}

              <div className="form-grid-2">
                <div className="form-field"><label>Business ID</label><input value={creds[lcKey("lc_business_id")] || ""} onChange={e => setCreds({ ...creds, [lcKey("lc_business_id")]: e.target.value })} placeholder="Required for all auth modes" /></div>
                {credsEnv === "live" && (
                  <div className="form-field" style={{ display: "flex", alignItems: "flex-end" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
                      <div className="toggle-switch">
                        <input type="checkbox" checked={creds.enabled} onChange={e => setCreds({ ...creds, enabled: e.target.checked })} />
                        <span className="toggle-track" />
                      </div>
                      <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Enable auto-issue on signup</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save Credentials"}</button>
                <button type="button" className="btn btn-ghost" onClick={testConnection} disabled={testing}>
                  {testing ? "Testing…" : "Test Connection"}
                </button>
              </div>

              {testResult && (
                <div className={`test-results-panel ${testResult.ok ? "success" : "error"}`} style={{ marginTop: "1rem" }}>
                  <div className="test-results-header">
                    <span className="test-results-title">
                      {testResult.ok ? "Connected" : "Connection Failed"}
                    </span>
                  </div>
                  <div style={{ padding: "0.5rem 1rem 0.7rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>{testResult.msg}</div>
                </div>
              )}
            </form>
          )}

          {/* ── Template & Mapping (unified) ───── */}
          {section === "template" && (
            <div className="form-section">
              <div className="section-header-row">
                <div>
                  <h2>Template & Field Mapping</h2>
                  <p className="section-desc">
                    Pass design lives in the <strong>Litecard portal</strong>.
                    Here you link this venue to a template and choose which
                    member-data field feeds each template input.
                  </p>
                </div>
                <button type="button" className="btn btn-ghost" onClick={openBrowse}>
                  {isLinked ? "Change template" : "Select template"}
                </button>
              </div>

              {!isLinked && (
                <div className="empty-state" style={{ padding: "2rem", textAlign: "center", border: "1px dashed var(--border)", borderRadius: 8, background: "var(--bg-subtle)" }}>
                  <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.92rem" }}>
                    No template linked to this venue yet.
                  </p>
                  <button type="button" className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={openBrowse}>
                    Select template from Litecard
                  </button>
                </div>
              )}

              {isLinked && (
                <>
                  <div
                    className={`info-banner${templateError ? " is-error" : ""}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "1rem",
                      marginBottom: "1rem",
                      padding: "0.7rem 0.95rem",
                      background: templateError ? "var(--danger-soft)" : "var(--bg-subtle)",
                      border: `1px solid ${templateError ? "var(--danger)" : "var(--border)"}`,
                      borderRadius: 6,
                      fontSize: "0.85rem",
                      color: "var(--text)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      {templateLoading ? (
                        <span style={{ color: "var(--text-secondary)" }}>Loading template from Litecard…</span>
                      ) : templateError ? (
                        <span style={{ color: "var(--danger)" }}>
                          Can't reach Litecard — {templateError}
                        </span>
                      ) : liveTemplate ? (
                        <>
                          Linked to <strong>{liveTemplate.name || "(unnamed)"}</strong>
                          <span style={{ marginLeft: 8, fontFamily: "monospace", color: "var(--text-muted)", fontSize: "0.78rem" }}>
                            {linkedTemplateId}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "var(--text-secondary)" }}>Template not loaded</span>
                      )}
                    </div>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={loadLiveTemplate} disabled={templateLoading}>
                      {Ico.refresh} <span style={{ marginLeft: 4 }}>Refresh</span>
                    </button>
                  </div>

                  {liveTemplate && (
                    <>
                      <fieldset className="dc-fieldset">
                        <legend>Pass fields</legend>
                        <p className="section-desc" style={{ marginBottom: "0.75rem" }}>
                          Each row is a field Litecard renders on the pass.
                          Pick the member-data source for each editable field.
                          Required fields must be mapped before passes can be issued.
                        </p>

                        <div className="table-responsive">
                          <table className="data-table" style={{ fontSize: "0.86rem" }}>
                            <thead>
                              <tr>
                                <th style={{ minWidth: 200 }}>Field</th>
                                <th style={{ width: 110 }}>Type</th>
                                <th style={{ width: 180 }}>Location</th>
                                <th>Mapped from</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(liveTemplate.dataFields || []).map(f => {
                                // Built-in card-owner fields (firstName/lastName/email/phone)
                                // are sent from the member record automatically — the backend
                                // ignores any mapping selection for them. Render them as
                                // "Auto · …" instead of an editable dropdown so admins don't
                                // think they need to configure something here.
                                const isBuiltIn = ["firstName", "lastName", "email", "phone"].includes(f.key);
                                const editable = !!f.formFieldSettings && !["fullName"].includes(f.key) && !isBuiltIn;
                                const required = f.formFieldSettings?.rules?.includes("required");
                                const dtype = f.formFieldSettings?.dataType || "";
                                const apple = f.appleFieldSettings ? locationLabel(f.appleFieldSettings.location) : null;
                                const google = f.googleFieldSettings ? locationLabel(f.googleFieldSettings.location) : null;
                                return (
                                  <tr key={f.key}>
                                    <td>
                                      <div style={{ fontWeight: 600, color: "var(--text)" }}>
                                        {f.label || f.key}
                                        {required && <span style={{ color: "var(--danger)", marginLeft: 4 }}>*</span>}
                                      </div>
                                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "monospace", marginTop: 2 }}>
                                        {f.key}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge badge-muted" style={{ textTransform: "capitalize" }}>
                                        {editable ? (dtype || "text") : "Display"}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                                      {apple && <div>Apple: <strong style={{ color: "var(--text)" }}>{apple}</strong></div>}
                                      {google && <div>Google: <strong style={{ color: "var(--text)" }}>{google}</strong></div>}
                                      {!apple && !google && "—"}
                                    </td>
                                    <td style={{ minWidth: 240 }}>
                                      {editable ? (
                                        <AdminSelect
                                          value={mapping[f.key] || ""}
                                          onChange={v => setMap(f.key, v)}
                                          options={sourceFields}
                                          searchable
                                          placeholder="— Select source field —"
                                        />
                                      ) : isBuiltIn ? (
                                        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                                          Auto · {sourceByValue[f.key]?.label || f.key}
                                        </span>
                                      ) : (
                                        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                                          {f.key === "fullName" ? "Auto · First Name + Last Name" : "Display-only"}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div style={{ marginTop: "0.85rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                          <span>Barcode: <strong style={{ color: "var(--text)" }}>{liveTemplate.barcode?.type || "—"}</strong></span>
                          {liveTemplate.barcode?.fieldMap && (
                            <span>· encodes <strong style={{ color: "var(--text)" }}>{liveTemplate.barcode.fieldMap}</strong></span>
                          )}
                          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4 }}>
                            Edit design in Litecard portal {Ico.ext}
                          </span>
                        </div>

                        {missingRequired.length > 0 && (
                          <div style={{
                            marginTop: "0.85rem",
                            padding: "0.55rem 0.8rem",
                            background: "var(--warning-soft)",
                            border: "1px solid var(--warning)",
                            borderRadius: 6,
                            fontSize: "0.82rem",
                            color: "var(--warning)",
                          }}>
                            <strong>Missing mappings:</strong> {missingRequired.join(", ")} must be mapped before a pass can be issued.
                          </div>
                        )}

                        <div className="form-actions" style={{ marginTop: "1rem" }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveMapping}
                            disabled={saving || !dirty}
                            title={!dirty ? "No changes to save" : ""}
                          >
                            {saving ? "Saving…" : dirty ? "Save mapping" : "Saved"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={revertMapping}
                            disabled={!dirty || saving}
                          >
                            Revert
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={generatePreview}
                            disabled={previewing || dirty || missingRequired.length > 0}
                            title={dirty ? "Save mapping first" : missingRequired.length ? "Map required fields first" : ""}
                          >
                            {previewing ? "Creating preview…" : "Generate real preview"}
                          </button>
                        </div>

                        {previewLinks && (
                          <div className="dc-wallet-links" style={{ marginTop: "1.5rem" }}>
                            <div className="dc-wallet-buttons">
                              {previewLinks.appleLink && (
                                <a href={previewLinks.appleLink} target="_blank" rel="noopener noreferrer" className="dc-wallet-btn dc-wallet-apple">
                                  {Ico.apple} <span>Apple Wallet</span>
                                </a>
                              )}
                              {previewLinks.googleLink && (
                                <a href={previewLinks.googleLink} target="_blank" rel="noopener noreferrer" className="dc-wallet-btn dc-wallet-google">
                                  {Ico.google} <span>Google Wallet</span>
                                </a>
                              )}
                            </div>
                            {(previewLinks.appleLink || previewLinks.googleLink) && (
                              <div className="dc-qr-section">
                                <p className="section-desc" style={{ marginBottom: 8 }}>Scan to add:</p>
                                <div className="dc-qr-codes">
                                  {previewLinks.appleLink && (
                                    <div className="dc-qr-item">
                                      <QRCodeSVG value={previewLinks.appleLink} size={110} bgColor="#ffffff" fgColor="#000000" level="M" />
                                      <span className="dc-qr-label">{Ico.apple} Apple</span>
                                    </div>
                                  )}
                                  {previewLinks.googleLink && (
                                    <div className="dc-qr-item">
                                      <QRCodeSVG value={previewLinks.googleLink} size={110} bgColor="#ffffff" fgColor="#000000" level="M" />
                                      <span className="dc-qr-label">{Ico.google} Google</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </fieldset>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Issued Passes ─────────────────── */}
          {section === "passes" && (
            <div className="form-section">
              <div className="section-header-row">
                <div>
                  <h2>Issued Passes</h2>
                  <p className="section-desc">{passTotal} total pass{passTotal !== 1 ? "es" : ""} issued for this venue</p>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={syncPassesFromLitecard}
                  disabled={syncingPasses || !isLinked}
                  title={!isLinked ? "Link a template first" : "Pull all cards from Litecard into the database"}
                >
                  {syncingPasses ? "Syncing…" : "Sync from Litecard"}
                </button>
              </div>

              {passesError ? (
                <div className="toast toast-error">{passesError}</div>
              ) : passesLoading ? (
                <div className="loading-state"><div className="spinner" /></div>
              ) : passes.length === 0 ? (
                <div className="empty-state" style={{ padding: "2rem", textAlign: "center" }}>
                  <p style={{ color: "var(--text-muted)" }}>No passes issued yet. Passes are created automatically when members sign up (if enabled).</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Membership #</th>
                          <th>Status</th>
                          <th>Issued</th>
                          <th>Apple</th>
                          <th>Google</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {passes.map(p => (
                          <tr key={p.id}>
                            <td>{p.first_name || p.last_name ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                            <td>{p.membership_id && p.membership_id !== "-" ? <code>{p.membership_id}</code> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                            <td><span className={`badge badge-${p.status === "ACTIVE" ? "success" : "muted"}`}>{p.status}</span></td>
                            <td>{formatDateAU(p.created_at)}</td>
                            <td>{p.apple_link ? <a href={p.apple_link} target="_blank" rel="noopener noreferrer" className="btn-icon-mini">{Ico.apple}</a> : "—"}</td>
                            <td>{p.google_link ? <a href={p.google_link} target="_blank" rel="noopener noreferrer" className="btn-icon-mini">{Ico.google}</a> : "—"}</td>
                            <td>
                              <button className="btn btn-ghost btn-xs" onClick={() => togglePassStatus(p)}>
                                {p.status === "ACTIVE" ? "Deactivate" : "Activate"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {passTotal > 25 && (
                    <div className="pagination" style={{ marginTop: "1rem", display: "flex", gap: 8, justifyContent: "center" }}>
                      <button className="btn btn-ghost btn-sm" disabled={passPage === 0} onClick={() => loadPasses(passPage - 1)}>← Prev</button>
                      <span style={{ lineHeight: "36px", fontSize: "0.85rem", color: "var(--text-muted)" }}>Page {passPage + 1} of {Math.ceil(passTotal / 25)}</span>
                      <button className="btn btn-ghost btn-sm" disabled={(passPage + 1) * 25 >= passTotal} onClick={() => loadPasses(passPage + 1)}>Next →</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Template browser modal */}
      {browseOpen && (
        <div
          className="modal-overlay"
          onClick={() => !selectingId && setBrowseOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div
            className="modal-panel"
            onClick={e => e.stopPropagation()}
            style={{
              background: "var(--bg-surface)",
              color: "var(--text)",
              borderRadius: 10,
              width: "min(780px, 94vw)",
              maxHeight: "84vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
              border: "1px solid var(--border)",
            }}
          >
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text)" }}>Litecard templates</h3>
                <p className="section-desc" style={{ margin: "0.2rem 0 0" }}>Pick which template this venue uses. Template design is edited in the Litecard portal.</p>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setBrowseOpen(false)} disabled={!!selectingId}>Close</button>
            </div>
            <div style={{ overflowY: "auto", padding: "0.75rem 1.25rem 1rem" }}>
              {browseLoading && <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>Loading templates…</div>}
              {!browseLoading && !remoteTemplates.length && (
                <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
                  No templates found in this Litecard account.
                </div>
              )}
              {!browseLoading && remoteTemplates.map(t => {
                const linked = linkedTemplateId === t.id;
                return (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: "0.9rem", padding: "0.75rem 0", borderBottom: "1px solid var(--border-light)" }}>
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: "var(--bg-muted)", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {t.logoImage
                        ? <img src={t.logoImage} alt="" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                        : <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>no logo</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text)" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                        {linked && (
                          <span
                            className="badge badge-success"
                            style={{ fontSize: "0.7rem", padding: "0.1rem 0.5rem", borderRadius: 999 }}
                          >
                            Current
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.businessName || "—"}{t.description ? ` · ${t.description}` : ""}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "monospace", marginTop: 2 }}>{t.id}</div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!!selectingId || linked}
                        onClick={() => selectTemplate(t.id)}
                      >
                        {selectingId === t.id ? "Linking…" : linked ? "In use" : "Use"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
